import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';
import { Authenticate } from 'src/app/models/server/authenticate';
import { OutputWebSocketMessage } from 'src/app/models/ws/output-websocket-message';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { FoldersService } from 'src/app/services/http//folders.service';
import { FolderDocumentsService } from 'src/app/services/http/folder-documents.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { TChatStateService } from 'src/app/services/state/tchat-state.service';
import { WebSocketTchatService } from 'src/app/services/ws/websocket-tchat.service';
import { FormFocusDirective } from '../../../core/directives/form-focus.directive';
import { SavedTchatRoom } from '../../../models/server/saved-tchat-room';
import { TchatMessage } from '../../../models/server/tchat-message';
import { TchatRoom } from '../../../models/server/tchat-room';
import { TchatUser } from '../../../models/server/tchat-user';
import { TchatRoomMessagesComponent } from '../tchat-room-messages/tchat-room-messages.component';

@Component({
    selector: 'app-tchat-room',
    templateUrl: './tchat-room.component.html',
    styleUrls: ['./tchat-room.component.scss'],
    imports: [
        MatTooltipModule,
        MatIconModule,
        NgClass,
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatInputModule,
        MatAutocompleteModule,
        MatOptionModule,
        FaIconComponent,
        MatChipsModule,
        TchatRoomMessagesComponent,
        AsyncPipe,
    ]
})
export class TchatRoomComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    public faCaretDown = faCaretDown;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * La conversation affichée.
     */
    private _room: TchatRoom;

    /**
     * Récupère la conversation affichée.
     */
    get room(): TchatRoom {
        return this._room;
    }

    /**
     * Spécifie la conversation affichée.
     * @room : Instance de la conversation.
     */
    @Input()
    set room(room: TchatRoom) {
        // room.messages.forEach(m => {
        // 	m.source = this.getSource(m);
        // });

        // room.target = this.getTarget(room)

        this._room = room;
        this.roomUsers = [];
        this.selectableUsers = this.tchatService.getUsers();
        this.BuildSelectableUsersList();
    }

    /**
     * Liste des utilisateurs pouvant être ajoutés à la conversation.
     */
    public selectableUsers: TchatUser[];

    /**
     * Liste de tous les utilisateurs de la conversation.
     */
    public roomUsers: TchatUser[] = [];

    /**
     * Valeur indiquant si le formulaire de recherche est visible.
     */
    public isSearchFormVisible: boolean = false;

    /**
     * Valeur indiquant si la liste des contacts est déployée.
     */
    public isContactsListExpanded: boolean = false;

    /**
     * Liste des contacts correspondant au critère de recherche.
     */
    public contactSearchResult: string[];

    /**
     * Contacts affichées dans la liste déroulante.
     */
    filteredContacts: Observable<TchatUser[]>;

    /**
     * Valeur indiquant si la liste des contacts correspondant au critère de recherche est visible.
     */
    public contactSearchResultVisible: boolean = false;

    /**
     * Formulaire de saisie des messages.
     */
    messageForm: UntypedFormGroup;

    /**
     * Formulaire de recherche des contacts.
     */
    searchContactForm: UntypedFormGroup;

    public connectedUser: Authenticate;

    /**
     * Constructeur.
     * @param tchatService
     * @param webSocketTchatService
     * @param dialoguService
     * @param foldersService
     * @param connectedUserStateService
     */
    constructor(
        protected tchatService: TChatStateService,
        protected webSocketTchatService: WebSocketTchatService,
        protected dialoguService: DialogService,
        protected foldersService: FoldersService,
        protected connectedUserStateService: ConnectedUsersStateService,
        protected notificationService: NotificationService,
        protected eventsManagerService: EventsManagerService,
        protected folderDocumentsService: FolderDocumentsService
    ) {
        this.connectedUser = this.connectedUserStateService.getUserConnected();
        this.selectableUsers = this.tchatService.getUsers();
        this.BuildSelectableUsersList();
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.messageForm = new UntypedFormGroup({});
        this.messageForm.addControl('messageControlName', new UntypedFormControl());

        this.searchContactForm = new UntypedFormGroup({});
        this.searchContactForm.addControl('userControlName', new UntypedFormControl());

        // S'aboone aux événements de saisie de l'utilisateur.
        this.filteredContacts = this.searchContactForm.controls['userControlName'].valueChanges.pipe(
            startWith(''),
            tap((value: string) => console.log('Change value : ', value)),
            map((value: string) => {
                //if (typeof value === 'string') {
                return this.filter((value as string) || '');
                //}
            })
        );
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton d'envoi du message.
     */
    public onMessageSend() {
        let currentMessage = this.messageForm.controls['messageControlName'].value;
        this.messageForm.controls['messageControlName'].setValue(null);

        if (this.roomUsers.length !== 0) {
            this.roomUsers.forEach((user) => {
                this.room.users.push(user);
            });
        }

        let source = this.room.users.find((x) => x.login == this.connectedUserStateService.getUserConnected().login);
        let targets = this.room.users.filter((x) => x.login != this.connectedUserStateService.getUserConnected().login);

        let message: TchatMessage = new TchatMessage(this.room.guid, source, targets, new Date(), currentMessage);

        if (this.room.messages.length == 0) {
            // création de la room avec le message
            let room: TchatRoom = new TchatRoom(this.room.users);
            room.organizationId = this.connectedUser.organization.id;
            room.target = this.tchatService.getRoomTarget(room);
            room.messages.push(message);
            this.webSocketTchatService.sendMessageRequest(new OutputWebSocketMessage('createRoom', room));
        } else {
            message.organizationId = this.connectedUser.organization.id;
            this.webSocketTchatService.sendMessageRequest(new OutputWebSocketMessage('newMessage', message));
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton d'ajout d'un contact.
     */
    public onAddContact(): void {
        this.isSearchFormVisible = true;
        setTimeout(() => {
            let input = document.getElementById('contact-add-input') as HTMLInputElement;
            input.focus();
        }, 100);
    }

    /**
     * Appelée lorsque l'utilisateur saisi du texte dans le champ de recherche.
     */
    public onSearchInputChange(): void {
        let currentValue = this.searchContactForm.controls['userControlName'].value.toLowerCase();
        this.contactSearchResultVisible = true;
        this.contactSearchResult = this.selectableUsers
            .filter((x) => x.login.toLowerCase().includes(currentValue) || x.displayName.toLowerCase().includes(currentValue))
            .map((x) => x.displayName);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Sauver'.
     */
    public onSave(): void {
        this.dialoguService.openSaveTchat().subscribe((x) => {
            if (!!x) {
                this.saveTchat(x.viewId, x.folderId, x.title);
            }
        });
    }

    /**
     * Retire un utilisateur de la conversation.
     * @param user : Utilisateur à retirer.
     */
    public removeUser(user: TchatUser): void {
        const index = this.roomUsers.indexOf(user);

        if (index >= 0) {
            // Retire l'utilisateur de la conversation.
            this.roomUsers.splice(index, 1);

            // Remet l'utilisateur dans la liste des utilisateurs pouvant être ajoutés.
            this.selectableUsers.push(user);
        }

        this.BuildSelectableUsersList();
    }

    public onContactSelected(event: MatAutocompleteSelectedEvent) {
        this.addUser(event.option.value);
    }

    /**
     * Ajoute un utilisateur à la conversation.
     * @param userName : Nom de l'utilisateur.
     */
    public addUser(userLogin: string): void {
        let user = this.selectableUsers.find((u) => u.login == userLogin);

        if (!!user) {
            this.roomUsers.push(user);
        }

        this.searchContactForm.controls['userControlName'].setValue(null);
        this.contactSearchResultVisible = false;
        this.contactSearchResult = [];
        this.isSearchFormVisible = false;

        this.BuildSelectableUsersList();
    }

    /**
     * Sauve la conversation en tant que document du dossier spécifié.
     * @param folderId : Identifiant du dossier auquel la conversation doit être ajoutée.
     * @param title : Intitulé du document auquel sera rattaché la conversation t'chat.
     */
    private saveTchat(viewId: number, folderId: string, title: string) {
        let savedRoom = SavedTchatRoom.createFormCopy(this.room);
        savedRoom.title = title;
        this.foldersService
            .saveTchat(viewId, folderId, savedRoom)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((document) => {
                this.refreshDocuments(viewId, folderId, document.id);
            });
    }

    private refreshDocuments(viewId: number, folderId: string, selectedDocumentId: string = null) {
        this.eventsManagerService.emitHistorySectionUpdatedEvent(viewId, folderId);

        this.folderDocumentsService.list(viewId, folderId).subscribe((documents) => {
            this.eventsManagerService.emitDocumentsUpdatedEvent(folderId, documents, selectedDocumentId);
        });

        this.eventsManagerService.emitListFolderUpdatedEvent(viewId, folderId);
        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(viewId, folderId);
    }

    /**
     * Retire les utilisateurs de la conversation de la liste des utilisateurs selectionnables.
     */
    private BuildSelectableUsersList(): void {
        let removedUsers = this.roomUsers.slice();
        removedUsers.push(this.tchatService.getUser(this.connectedUserStateService.getUserConnected().login));
        this.selectableUsers = this.selectableUsers.filter((x) => !removedUsers.includes(x));
    }

    /**
     * Appelée lorsque la liste déroulante se ferme.
     */
    public closed(): void {}

    /**
     * Appelée lorsque la liste déroulante s'ouvre.
     */
    public opened(): void {}

    /**
     * Appelé par l'autocomplete pour déterminer le libellé à afficher pour un utilisateur.
     * @param user : L'utilisateur à afficher
     * @returns : Le libellé de l'utilisateur.
     */
    displayLabel(user: TchatUser) {
        return user ? user.displayName : '';
    }

    /**
     * Filtre les contacts correspondant au texte saisie par l'utilisateur.
     * @param value : texte saisie par l'utilisateur.
     * @returns Les contacts filtrées.
     */
    private filter(value: string): TchatUser[] {
        const filterValue = value.toLowerCase();
        return this.selectableUsers.filter((x) => x.login.toLowerCase().includes(filterValue) || x.displayName.toLowerCase().includes(filterValue));
    }
}
